import { Fragment, useState, useEffect, useRef } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { Stack, Title, Text, Button, Group, Indicator, ThemeIcon, Paper, Tooltip, Slider, Box, Badge, Textarea, Switch, SegmentedControl, Chip, Collapse, UnstyledButton } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconArrowLeft, IconArrowRight, IconCalendar, IconChefHat, IconInfoCircle, IconToolsKitchen2, IconPencil } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { CustomMeal, MealPlan, MealPlanEvent, MealPrepPreferences, PenciledMealMemory, PlannedPenciledMeal, AdditionalMealPreferences } from '../../../types/mealPlan';
import { CustomMealDrawer } from '../CustomMealDrawer';
import { useMealPlanning } from '../../../hooks/useMealPlanning';
import { useAuth } from '../../../hooks/useAuth';
import { useAppUser } from '../../../hooks/useAppUser';
import { useFeatureFlag } from '../../../hooks/useFeatureFlags';
import { effectiveRotationSize, MAX_ROTATION_SIZE } from '../../../utils/rotationSize';
import { SpecialConsiderationsStep } from './SpecialConsiderationsStep';

export type WeekShapeValue = {
  dateRange: [string | null, string | null];
  uniqueMeals?: number;
  mealPrepPreferences?: MealPrepPreferences;
  quickMealDays?: string[];
  additionalMeals?: AdditionalMealPreferences;
  prepDays?: string[];
  penciledMeals?: PlannedPenciledMeal[];
};

const noop = () => {};

// Checked Mantine chips swap padding and insert a check icon, so every toggle
// changes the chip's width and reflows the row (and the card below it).
// Constant padding + no icon keeps the layout still; checked state reads
// through the fill color.
const STABLE_CHIP_STYLES = {
  label: { paddingInline: 'var(--chip-padding)' },
  iconWrapper: { display: 'none' },
} as const;

// Phone widths: the meal chips keep one row by shedding padding, not letters
const NARROW_CHIP_STYLES = {
  label: { paddingInline: 8, fontSize: 13 },
  iconWrapper: { display: 'none' },
} as const;

type WeekShapeStepProps = {
  value: WeekShapeValue;
  onChange: (value: WeekShapeValue) => void;
  onNext: () => void;
  onBack: () => void;
  // Events planned on the extras step (read-only here): a day with an event
  // can't be penciled — generation would silently drop the event, since it
  // doesn't plan penciled days
  plannedEvents?: MealPlanEvent[];
};

export const WeekShapeStep = ({
  value,
  onChange,
  onNext,
  onBack,
  plannedEvents,
}: WeekShapeStepProps) => {
  const { user, loading: authLoading } = useAuth();
  const { user: appUser } = useAppUser();
  const { getUserMealPlans, getPenciledMeals } = useMealPlanning();
  const additionalMealsEnabled = useFeatureFlag('additional_meals');
  // Standing cooking-style traits from the family profile: the source of the
  // wizard's weekly defaults (phase B of the weekly-vs-standing split)
  const cookingStyle = appUser?.familyProfile?.cookingStyle;
  const [existingMealPlans, setExistingMealPlans] = useState<MealPlan[]>([]);
  const hasPrePopulated = useRef(false);
  // Latest value for async callbacks: the date-default fetch completes after
  // other effects may have written to value (e.g. meal prep pre-population),
  // and merging onto a stale closure would silently wipe those writes.
  const valueRef = useRef(value);
  valueRef.current = value;

  const dateRange = value.dateRange;
  // Pointer-adaptive copy: "tap" for touch devices, "click" for mouse/trackpad
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  // Phone widths: the three meal chips at md wrap "Dinners" onto its own
  // line; smaller chips with compressed padding keep them on one row
  const isNarrowScreen = useMediaQuery('(max-width: 575px)');

  // Helper function to format dates in short format
  const formatDateForDisplay = (dateString: string) => {
    const [, month, day] = dateString.split('-').map(Number);
    // Short format: "12/25" or "1/5"
    return `${month}/${day}`;
  };

  // Find the first available contiguous Monday-Sunday period that doesn't overlap with existing meal plans
  const findNextAvailableWeek = (existingMealPlans: MealPlan[]): [string, string] => {
    // Format from LOCAL date components. toISOString() converts to UTC and
    // the Dates below carry the user's wall-clock time, so an evening user
    // west of UTC would get the whole default week shifted a day forward.
    const toLocalDateString = (date: Date): string =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
        date.getDate()
      ).padStart(2, '0')}`;
    // Helper function to get Monday-Sunday week dates starting from a given Monday
    const getWeekRange = (monday: Date): [Date, Date] => {
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return [monday, sunday];
    };

    // Helper function to check if two date ranges overlap
    const dateRangesOverlap = (start1: Date, end1: Date, start2: Date, end2: Date): boolean => {
      // Two ranges overlap if one starts before the other ends, and vice versa
      // No overlap if: end1 < start2 OR end2 < start1
      // So overlap is: NOT (end1 < start2 OR end2 < start1)
      // Which simplifies to: end1 >= start2 AND end2 >= start1
      return end1 >= start2 && end2 >= start1;
    };

    // Helper function to find the next Monday from a given date (or the date itself if it's Monday)
    const getNextMonday = (fromDate: Date): Date => {
      const date = new Date(fromDate);
      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

      if (dayOfWeek === 1) {
        // It's already Monday, return next Monday instead to avoid same week
        date.setDate(date.getDate() + 7);
        return date;
      } else if (dayOfWeek === 0) {
        // It's Sunday, next Monday is tomorrow
        date.setDate(date.getDate() + 1);
        return date;
      } else {
        // It's Tuesday-Saturday, find next Monday
        const daysUntilMonday = 8 - dayOfWeek; // 8-2=6 for Tue, 8-3=5 for Wed, etc.
        date.setDate(date.getDate() + daysUntilMonday);
        return date;
      }
    };

    // Filter out failed meal plans and convert dates to Date objects
    const activePlans = existingMealPlans
      .filter(plan => plan.generationStatus.status !== 'failed')
      .map(plan => {
        const [startYear, startMonth, startDay] = plan.startDate.split('-').map(Number);
        const [endYear, endMonth, endDay] = plan.endDate.split('-').map(Number);
        return {
          start: new Date(startYear, startMonth - 1, startDay),
          end: new Date(endYear, endMonth - 1, endDay)
        };
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime()); // Sort by start date

    // Start from the next Monday after today. Normalize to midnight: plan
    // dates parse to midnight, and a candidate carrying the wall-clock time
    // reads a plan that ENDS on the candidate Monday as non-overlapping
    // (plan end 00:00 < candidate 14:37), seeding the default onto an
    // occupied day.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let candidateMonday = getNextMonday(today);

    // Check up to 52 weeks in the future to find an available Monday-Sunday period
    for (let week = 0; week < 52; week++) {
      const [weekStart, weekEnd] = getWeekRange(candidateMonday);

      // Check if this week overlaps with any existing meal plan
      const hasOverlap = activePlans.some(plan =>
        dateRangesOverlap(weekStart, weekEnd, plan.start, plan.end)
      );

      if (!hasOverlap) {
        // Found an available week
        return [
          toLocalDateString(weekStart),
          toLocalDateString(weekEnd)
        ];
      }

      // Move to next Monday
      candidateMonday.setDate(candidateMonday.getDate() + 7);
    }

    // Fallback: if no available week found in 52 weeks (highly unlikely),
    // return the Monday-Sunday week starting from today + 1 year
    const fallbackMonday = getNextMonday(today);
    fallbackMonday.setFullYear(fallbackMonday.getFullYear() + 1);
    const [fallbackStart, fallbackEnd] = getWeekRange(fallbackMonday);

    return [
      toLocalDateString(fallbackStart),
      toLocalDateString(fallbackEnd)
    ];
  };

  // Fetch existing meal plans and set default date range. The default is
  // one-shot (ref guard): the input is clearable, and without the guard this
  // effect would instantly re-fill a cleared input with the smart default,
  // making it look like clearing doesn't work.
  const hasSetDefaultRange = useRef(false);
  useEffect(() => {
    // Don't fetch meal plans until authentication is complete
    if (authLoading || !user) {
      return;
    }

    const fetchMealPlansAndSetDefault = async () => {
      try {
        const plans = await getUserMealPlans();
        setExistingMealPlans(plans);

        // The default is one-shot: the first run consumes the guard whether
        // or not it fills anything (the step often mounts with dates already
        // chosen), so a user clearing the input never gets it repopulated
        if (!hasSetDefaultRange.current) {
          hasSetDefaultRange.current = true;
          const latest = valueRef.current;
          if (!latest.dateRange[0] && !latest.dateRange[1]) {
            const nextAvailableWeek = findNextAvailableWeek(plans);
            onChange({ ...latest, dateRange: nextAvailableWeek });
          }
        }
      } catch (error) {
        console.error('Failed to fetch meal plans:', error);
        // Fallback: set next Monday-Sunday if fetch fails
        if (!hasSetDefaultRange.current) {
          hasSetDefaultRange.current = true;
          const latest = valueRef.current;
          if (!latest.dateRange[0] && !latest.dateRange[1]) {
            const nextAvailableWeek = findNextAvailableWeek([]);
            onChange({ ...latest, dateRange: nextAvailableWeek });
          }
        }
      }
    };
    fetchMealPlansAndSetDefault();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, getUserMealPlans, dateRange, onChange]);

  // Pre-populate meal prep preferences from user profile on first load
  useEffect(() => {
    if (!hasPrePopulated.current && !value.mealPrepPreferences?.generalPreferences && appUser?.familyProfile?.mealPrepPreferences?.generalPreferences) {
      onChange({ ...value, mealPrepPreferences: appUser.familyProfile.mealPrepPreferences });
      hasPrePopulated.current = true;
    }
  }, [appUser, onChange, value]);

  // Calculate total days in the meal plan
  const getTotalDays = (range: [string | null, string | null]): number => {
    if (!range[0] || !range[1]) {
      return 7; // Default to 7 days if no date range
    }

    const startDate = new Date(range[0]);
    const endDate = new Date(range[1]);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
    return diffDays;
  };

  const totalDays = getTotalDays(dateRange);
  // Cap at totalDays - 1: one unique meal per day is the same as not meal
  // prepping, so that state is unrepresentable while "Yes" is selected
  // Penciled-in nights don't need cooking, so meal prep math runs on the
  // remaining days
  const cookingDays = Math.max(totalDays - (value.penciledMeals?.length ?? 0), 1);
  // Unfloored twin of cookingDays for display math: an all-penciled week
  // really has zero cook nights, and copy must not claim one
  const cookNights = Math.max(totalDays - (value.penciledMeals?.length ?? 0), 0);
  const maxUniqueMeals = Math.max(cookingDays - 1, 1);
  // Default to batching about half the days so "Yes" enables actual meal prep
  const defaultUniqueMeals = Math.min(Math.ceil(totalDays / 2), maxUniqueMeals);
  const uniqueMeals = Math.min(value.uniqueMeals ?? defaultUniqueMeals, maxUniqueMeals);

  // Plan days in order with short date labels ("9/8"), for the drawer's
  // night picker and the summary's leftover-lunch math
  const planDays = (() => {
    const [start, end] = dateRange;
    if (!start || !end) return [] as { day: string; dateLabel: string }[];
    const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const [startYear, startMonth, startDay] = start.split('-').map(Number);
    const days: { day: string; dateLabel: string }[] = [];
    for (let i = 0; i < totalDays && i < 7; i++) {
      const current = new Date(startYear, startMonth - 1, startDay + i);
      const dayOfWeek = current.getDay();
      days.push({
        day: DAYS[dayOfWeek === 0 ? 6 : dayOfWeek - 1],
        dateLabel: `${current.getMonth() + 1}/${current.getDate()}`,
      });
    }
    return days;
  })();

  // The cooks-to-meals summary: every meal is either a cook or served from
  // one (leftovers). Batch dinners collapse dinner cooks to uniqueMeals;
  // leftover lunches add meals but zero cooks. Rotations count meal-for-cook
  // under fresh style; under batch style each rotation block is one cook
  // (generation batches prep-friendly blocks as anchor + references), so
  // cooks collapse to roughly the rotation size. Derived display only — it
  // bridges the concepts without coupling any controls.
  const plannedBreakfast = value.additionalMeals?.breakfast;
  const plannedLunch = value.additionalMeals?.lunch;
  // Effective rotation sizes: the weekly override when set, else the
  // plan-length default, clamped to the plan (shared mirror of the backend
  // curve). Options cap at MAX_ROTATION_SIZE: bigger rotations dilute the
  // repeatable positioning and outrun the slot pools.
  const breakfastRotation = effectiveRotationSize(
    'breakfast',
    totalDays,
    plannedBreakfast?.rotationSize
  );
  const lunchRotation = effectiveRotationSize(
    'lunch',
    totalDays,
    plannedLunch?.rotationSize
  );
  const maxRotationChoice = Math.max(1, Math.min(MAX_ROTATION_SIZE, totalDays));
  const rotationChoices = Array.from({ length: maxRotationChoice }, (_, i) =>
    String(i + 1)
  );
  const breakfastBatches = !!plannedBreakfast && plannedBreakfast.batch !== false;
  const lunchRotationBatches =
    plannedLunch?.mode === 'rotation' && plannedLunch.batch !== false;
  // Penciled nights are covered outside the plan: no dinner cook, no meal
  const dinnerCooks = cookNights === 0 ? 0 : value.uniqueMeals ?? cookNights;
  // Leftover lunches mirror generation's rule: each day draws from the most
  // recent cooked (non-penciled) dinner within the 3-day freshness cap, so
  // day 1 gets nothing and a run of penciled nights can leave gaps
  const leftoverLunchCount = (() => {
    if (plannedLunch?.mode !== 'leftovers') return 0;
    const penciledSet = new Set((value.penciledMeals ?? []).map((m) => m.day));
    let count = 0;
    for (let i = 1; i < planDays.length; i++) {
      for (let j = i - 1; j >= 0 && i - j <= 3; j--) {
        if (!penciledSet.has(planDays[j].day)) {
          count++;
          break;
        }
      }
    }
    return count;
  })();
  const lunchMeals = plannedLunch
    ? plannedLunch.mode === 'leftovers'
      ? leftoverLunchCount
      : totalDays
    : 0;
  const lunchCooks =
    plannedLunch && plannedLunch.mode === 'rotation'
      ? lunchRotationBatches
        ? lunchRotation
        : totalDays
      : 0;
  const breakfastCount = plannedBreakfast ? totalDays : 0;
  const breakfastCooks = plannedBreakfast
    ? breakfastBatches
      ? breakfastRotation
      : totalDays
    : 0;
  const totalMeals = cookNights + breakfastCount + lunchMeals;
  const totalCooks = dinnerCooks + breakfastCooks + lunchCooks;
  // Candidate batch-cooking dates: the day before the plan starts (night-
  // before prep) through the second-to-last day (a cook on the final day
  // has nothing left to feed ahead). Concrete dates, not weekdays — a
  // Mon-Sun plan contains two Sundays-adjacent candidates and they mean
  // different things.
  const prepDateOptions: { date: string; label: string }[] = (() => {
    if (!dateRange[0] || !dateRange[1]) return [];
    const [sy, sm, sd] = dateRange[0].split('-').map(Number);
    const [ey, em, ed] = dateRange[1].split('-').map(Number);
    const end = new Date(ey, em - 1, ed);
    const options: { date: string; label: string }[] = [];
    for (let d = new Date(sy, sm - 1, sd - 1); d < end; d.setDate(d.getDate() + 1)) {
      const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      options.push({
        date,
        label: `${d.toLocaleDateString('en-US', { weekday: 'short' })} ${d.getMonth() + 1}/${d.getDate()}`,
      });
    }
    return options;
  })();

  // Prep days only matter when there are MOVABLE cooking blocks to place:
  // batch dinners and make-ahead rotations. Fresh dinners feeding leftover
  // lunches used to count too, but with every night cooking there is no
  // scheduling freedom, so the chips did nothing. The dinner-side term needs
  // at least two cook nights to mean anything, checked here rather than
  // trusted from state: a restored request can carry batch flags alongside
  // an all-penciled week.
  const anyBatchCooking =
    (cookNights > 1 && value.uniqueMeals !== undefined) ||
    breakfastBatches ||
    lunchRotationBatches;

  // The summary sentence IS the adjustment surface: each clause is tappable
  // and opens ONLY that meal's controls below it. This replaced an "Adjust
  // for this week" panel that rendered every meal's controls at once - six
  // input vocabularies simultaneously - when the actual journey is "read the
  // narration, object to one clause". Parts without a target render as plain
  // text (degenerate weeks with nothing to choose). Chronological order,
  // matching the plan view's tabs.
  type AdjustTarget = 'dinner' | 'breakfast' | 'lunch' | 'prepdays';
  const dinnerAdjustable = totalDays > 1 && cookNights > 1;
  const prepDaysAdjustable = anyBatchCooking && prepDateOptions.length > 0;

  // Terse noun phrases on purpose: with all three meals planned this line
  // was two-plus wrapped lines of prose on mobile
  const weekSummaryParts: { label: string; target?: AdjustTarget }[] = [];
  if (plannedBreakfast) {
    weekSummaryParts.push({
      label: breakfastBatches
        ? `${breakfastRotation} make-ahead breakfast${breakfastRotation !== 1 ? 's' : ''}`
        : `${breakfastRotation} breakfast${breakfastRotation !== 1 ? 's' : ''} on rotation`,
      target: 'breakfast',
    });
  }
  if (plannedLunch) {
    weekSummaryParts.push({
      label:
        plannedLunch.mode === 'leftovers'
          ? leftoverLunchCount < totalDays - 1
            ? `leftover lunches on ${leftoverLunchCount} day${leftoverLunchCount !== 1 ? 's' : ''}`
            : 'leftover lunches'
          : lunchRotationBatches
            ? `${lunchRotation} make-ahead lunch${lunchRotation !== 1 ? 'es' : ''}`
            : `${lunchRotation} lunch${lunchRotation !== 1 ? 'es' : ''} on rotation`,
      target: 'lunch',
    });
  }
  if (totalDays === 1) {
    weekSummaryParts.push({ label: 'one dinner for your day' });
  } else if (cookNights === 0) {
    weekSummaryParts.push({ label: 'your own meals every night' });
  } else if (value.uniqueMeals !== undefined) {
    weekSummaryParts.push({
      label: `${uniqueMeals} dinner cook${uniqueMeals !== 1 ? 's' : ''} for ${cookNights} night${cookNights !== 1 ? 's' : ''}`,
      ...(dinnerAdjustable ? { target: 'dinner' as const } : {}),
    });
  } else {
    weekSummaryParts.push({
      label:
        cookNights < totalDays
          ? `fresh dinners on ${cookNights} night${cookNights !== 1 ? 's' : ''}`
          : 'fresh dinners',
      ...(dinnerAdjustable ? { target: 'dinner' as const } : {}),
    });
  }
  if (prepDaysAdjustable) {
    const labels = (value.prepDays ?? [])
      .map(d => prepDateOptions.find(o => o.date === d)?.label)
      .filter((l): l is string => !!l);
    // Unset still gets a clause: the one genuinely weekly control should
    // invite a tap rather than hide until it has a value
    weekSummaryParts.push({
      label: labels.length > 0 ? `prep on ${labels.join(', ')}` : 'any cooking days',
      target: 'prepdays',
    });
  }
  // The cooks-to-meals aggregate joins the summary line rather than getting
  // its own row; on a dinner-only plan it restates the dinner part, so it
  // only appears once breakfast or lunch is in the mix

  // Which clause's controls are open (one at a time; tap again to close)
  const [activeAdjust, setActiveAdjust] = useState<AdjustTarget | null>(null);

  // Shared by the first-time inline chooser and the adjust panel
  const setLunchMode = (mode: string) => {
    // Entering rotation keeps any weekly size override already on the lunch,
    // else seeds from the standing profile default, same as the chip
    const lunchRotationSeed =
      value.additionalMeals?.lunch?.rotationSize ??
      cookingStyle?.lunchRotationSize;
    onChange({
      ...value,
      additionalMeals: {
        ...value.additionalMeals,
        lunch:
          mode === 'rotation'
            ? {
                mode: 'rotation',
                batch: value.additionalMeals?.lunch?.batch ?? true,
                ...(lunchRotationSeed !== undefined && {
                  rotationSize: lunchRotationSeed,
                }),
              }
            : { mode: 'leftovers' },
      },
    });
  };

  // One-shot seeding of the profile's standing traits into this plan's
  // defaults, once dates exist. Only fills untouched fields; the ref guard
  // means clearing a seeded value never gets it re-seeded.
  const hasSeededCookingStyle = useRef(false);
  useEffect(() => {
    if (hasSeededCookingStyle.current) return;
    // cookingStyle is only ever written through flag-gated surfaces, but a
    // flagged-then-unflagged user still carries one; without this guard their
    // seeded prepDays would be silently stripped server-side at plan create
    if (!additionalMealsEnabled) return;
    if (!cookingStyle || !dateRange[0] || !dateRange[1]) return;
    hasSeededCookingStyle.current = true;

    const latest = valueRef.current;
    const updates: Partial<WeekShapeValue> = {};

    const [sy, sm, sd] = dateRange[0].split('-').map(Number);
    const [ey, em, ed] = dateRange[1].split('-').map(Number);
    const days =
      Math.round(
        (new Date(ey, em - 1, ed).getTime() - new Date(sy, sm - 1, sd).getTime()) /
          (24 * 60 * 60 * 1000)
      ) + 1;

    // Restored penciled nights (e.g. from the last-request prefill) don't
    // get cooked, so the seeded cook count runs on the remaining nights
    const seedCookNights = days - (latest.penciledMeals?.length ?? 0);
    if (cookingStyle.batchCooking && latest.uniqueMeals === undefined && seedCookNights > 1) {
      updates.uniqueMeals = Math.min(
        Math.ceil(seedCookNights / 2),
        Math.max(seedCookNights - 1, 1)
      );
    }

    if ((cookingStyle.prepWeekdays?.length ?? 0) > 0 && !latest.prepDays) {
      // Map preferred weekdays onto this plan's candidate cook dates (the day
      // before the plan starts through the second-to-last day)
      const prepSet = new Set(cookingStyle.prepWeekdays);
      const seeded: string[] = [];
      const end = new Date(ey, em - 1, ed);
      for (let d = new Date(sy, sm - 1, sd - 1); d < end; d.setDate(d.getDate() + 1)) {
        const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
        if (prepSet.has(weekday)) {
          seeded.push(
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
          );
        }
      }
      if (seeded.length > 0) {
        updates.prepDays = seeded;
      }
    }

    if (Object.keys(updates).length > 0) {
      onChange({ ...latest, ...updates });
    }
  }, [additionalMealsEnabled, cookingStyle, dateRange, onChange]);

  // Flag-off users keep the original standalone meal prep card (Yes/No)
  const [showMealPrepOptions, setShowMealPrepOptions] = useState(value.uniqueMeals !== undefined);

  // Pencil-in: nights the family already has covered. The drawer claims a
  // specific weekday; generation plans the rest of the week around it.
  const penciledMeals = value.penciledMeals ?? [];
  const [pencilDrawer, setPencilDrawer] = useState<{ day: string; dateLabel: string } | null>(null);
  const [savedPenciledMeals, setSavedPenciledMeals] = useState<PenciledMealMemory[] | null>(null);

  const openPencilDrawer = (day: string, dateLabel: string) => {
    setPencilDrawer({ day, dateLabel });
    if (savedPenciledMeals === null) {
      getPenciledMeals().then(setSavedPenciledMeals);
    }
  };

  // Penciling nights shrinks the cooking week, so the stored uniqueMeals must
  // shrink with it — the slider and its copy re-clamp on render, but the
  // picker and the submit read the stored value (same job as
  // handleRangeChange's clamp when the date range shrinks). Under two cook
  // nights batch cooking is meaningless, so it clears entirely (also like
  // the range handler).
  const clampUniqueMealsFor = (
    penciled: PlannedPenciledMeal[] | undefined
  ): number | undefined => {
    if (value.uniqueMeals === undefined) return undefined;
    const nextCookNights = totalDays - (penciled?.length ?? 0);
    if (nextCookNights <= 1) return undefined;
    return Math.min(value.uniqueMeals, Math.max(nextCookNights - 1, 1));
  };

  // Leftover lunches need another night's cooked dinner; penciling the week
  // down to a single cook night forces the fresh-lunch rotation, same as a
  // single-day plan (mirrors handleRangeChange's rule when the range shrinks)
  const clampAdditionalMealsFor = (
    penciled: PlannedPenciledMeal[] | undefined
  ): AdditionalMealPreferences | undefined => {
    const nextCookNights = totalDays - (penciled?.length ?? 0);
    if (nextCookNights <= 1 && value.additionalMeals?.lunch?.mode === 'leftovers') {
      return { ...value.additionalMeals, lunch: { mode: 'rotation' } };
    }
    return value.additionalMeals;
  };

  const handlePencilSubmit = (meal: CustomMeal, meta: { usedMemory: boolean; day?: string }) => {
    if (!meta.day || !pencilDrawer) return;
    const entry: PlannedPenciledMeal = { ...meal, day: meta.day };
    const next = [
      ...penciledMeals.filter((m) => m.day !== meta.day),
      entry,
    ];
    const nextUniqueMeals = clampUniqueMealsFor(next);
    onChange({
      ...value,
      penciledMeals: next,
      uniqueMeals: nextUniqueMeals,
      // Batch notes ride along with batch mode, as in the range handler
      // (only on an actual batch-to-cleared transition: fresh mode carries
      // profile-seeded notes that penciling must not wipe)
      mealPrepPreferences:
        value.uniqueMeals !== undefined && nextUniqueMeals === undefined
          ? undefined
          : value.mealPrepPreferences,
      additionalMeals: clampAdditionalMealsFor(next),
    });
    setPencilDrawer(null);
  };

  const removePenciledDay = (day: string) => {
    const next = penciledMeals.filter((m) => m.day !== day);
    onChange({
      ...value,
      penciledMeals: next.length > 0 ? next : undefined,
      uniqueMeals: clampUniqueMealsFor(next),
    });
    setPencilDrawer(null);
  };

  // Editing whichever meal claims the tapped day (if any)
  const editingMeal = pencilDrawer
    ? penciledMeals.find((m) => m.day === pencilDrawer.day)
    : undefined;
  const [showMealPrepTooltip, setShowMealPrepTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [showPencilTooltip, setShowPencilTooltip] = useState(false);
  const pencilTooltipRef = useRef<HTMLDivElement>(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setShowMealPrepTooltip(false);
      }
    };

    if (showMealPrepTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMealPrepTooltip]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pencilTooltipRef.current && !pencilTooltipRef.current.contains(event.target as Node)) {
        setShowPencilTooltip(false);
      }
    };
    if (showPencilTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPencilTooltip]);

  // Calculate disabled dates based on existing non-failed meal plans
  const getDisabledDates = () => {
    const disabledDates: Date[] = [];

    // Filter out failed meal plans
    const activePlans = existingMealPlans.filter(plan =>
      plan.generationStatus.status !== 'failed'
    );

    activePlans.forEach(plan => {
      // Parse date strings manually to avoid timezone issues
      const [startYear, startMonth, startDay] = plan.startDate.split('-').map(Number);
      const [endYear, endMonth, endDay] = plan.endDate.split('-').map(Number);

      const startDate = new Date(startYear, startMonth - 1, startDay);
      const endDate = new Date(endYear, endMonth - 1, endDay);

      // Add all dates in the range (inclusive)
      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        disabledDates.push(new Date(date));
      }
    });

    return disabledDates;
  };

  // Check if a date is covered by existing meal plans and return status info (for visual indicators)
  const getDateCoverageInfo = (dateString: string): { isCovered: boolean; status?: string; mealPlanId?: string } => {
    if (!dateString) return { isCovered: false };

    // Parse the input date string manually to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const inputDate = new Date(year, month - 1, day);

    // Check if this date is covered by any existing meal plan
    for (const plan of existingMealPlans) {
      if (plan.generationStatus.status === 'failed') continue;

      // Parse meal plan dates manually to avoid timezone issues
      const [startYear, startMonth, startDay] = plan.startDate.split('-').map(Number);
      const [endYear, endMonth, endDay] = plan.endDate.split('-').map(Number);
      const startDate = new Date(startYear, startMonth - 1, startDay);
      const endDate = new Date(endYear, endMonth - 1, endDay);

      // Check if input date falls within this meal plan's range
      if (inputDate >= startDate && inputDate <= endDate) {
        return { isCovered: true, status: plan.status, mealPlanId: plan.id };
      }
    }

    return { isCovered: false };
  };

  const handleDateRangeChange = (range: [string | null, string | null]) => {
    // Validate maximum 7 days
    if (range[0] && range[1]) {
      const [startYear, startMonth, startDay] = range[0].split('-').map(Number);
      const [endYear, endMonth, endDay] = range[1].split('-').map(Number);
      const startDate = new Date(startYear, startMonth - 1, startDay);
      const endDate = new Date(endYear, endMonth - 1, endDay);

      const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

      if (daysDiff > 7) {
        notifications.show({
          title: 'Invalid date range',
          message: 'Meal plans can be a maximum of 7 days.',
          color: 'red',
          autoClose: 4000,
        });
        return;
      }
    }

    // Keep uniqueMeals consistent with the new range
    let newUniqueMeals = value.uniqueMeals;
    let newMealPrepPreferences = value.mealPrepPreferences;
    let newAdditionalMeals = value.additionalMeals;
    if (range[0] && range[1]) {
      const newTotalDays = getTotalDays(range);
      if (newTotalDays === 1) {
        // Meal prep section is hidden for single-day plans
        newUniqueMeals = undefined;
        newMealPrepPreferences = undefined;
      } else if (newUniqueMeals !== undefined && newUniqueMeals > newTotalDays - 1) {
        // Clamp uniqueMeals when the plan shrinks below it
        newUniqueMeals = newTotalDays - 1;
      }
      // Leftover lunches need another night's cooked dinner; single-day
      // plans and ranges penciled down to one cook night fall back to a
      // fresh-lunch rotation
      const newCookNights = newTotalDays - (value.penciledMeals?.length ?? 0);
      if (newCookNights <= 1 && newAdditionalMeals?.lunch?.mode === 'leftovers') {
        newAdditionalMeals = { ...newAdditionalMeals, lunch: { mode: 'rotation' } };
      }
    }

    // Prep dates are concrete; drop any that fall outside the new range's
    // window (day before start through the day before the end)
    let newPrepDays = value.prepDays;
    if (newPrepDays && range[0] && range[1]) {
      const [sy, sm, sd] = range[0].split('-').map(Number);
      const dayBefore = new Date(sy, sm - 1, sd - 1);
      const windowStart = `${dayBefore.getFullYear()}-${String(dayBefore.getMonth() + 1).padStart(2, '0')}-${String(dayBefore.getDate()).padStart(2, '0')}`;
      newPrepDays = newPrepDays.filter(d => d >= windowStart && d < range[1]!);
      if (newPrepDays.length === 0) newPrepDays = undefined;
    } else if (newPrepDays && (!range[0] || !range[1])) {
      newPrepDays = undefined;
    }

    onChange({ ...value, dateRange: range, uniqueMeals: newUniqueMeals, mealPrepPreferences: newMealPrepPreferences, additionalMeals: newAdditionalMeals, prepDays: newPrepDays });
  };

  const handleNext = () => {
    if (!dateRange[0] || !dateRange[1]) {
      notifications.show({
        title: 'Date required',
        message: 'Please select both start and end dates for your meal plan.',
        color: 'red',
        autoClose: 4000,
      });
      return;
    }
    // The calendar disables covered dates at click time, but a range can
    // predate the plan that now covers it (restored wizard state, or a plan
    // created in another tab), and the backend would fail-fast the whole
    // creation. Re-check here where the user can still fix it. ISO date
    // strings compare lexicographically.
    const overlapping = existingMealPlans.find(
      (plan) =>
        plan.generationStatus.status !== 'failed' &&
        plan.startDate <= dateRange[1]! &&
        plan.endDate >= dateRange[0]!
    );
    if (overlapping) {
      notifications.show({
        title: 'Dates unavailable',
        message: `${formatDateForDisplay(overlapping.startDate)} to ${formatDateForDisplay(overlapping.endDate)} is already covered by another meal plan. Please pick different dates.`,
        color: 'red',
        autoClose: 6000,
      });
      return;
    }
    onNext();
  };

  const canProceed = dateRange[0] && dateRange[1];

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
      <Stack gap="md" ta="center" align="center">
        <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
          <IconCalendar size={40} />
        </ThemeIcon>
        <Stack gap="xs" align="center">
          <Title order={2} size="h1" fw={700} c="dark">
            Shape your week
          </Title>
          <Text size="lg" c="dimmed" ta="center" maw={400}>
            Choose your dates and set the pace of your meal plan
          </Text>
        </Stack>
      </Stack>

      {/* Date Selection */}
      <Paper
        radius="lg"
        p={{ base: 'lg', sm: 'xl' }}
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          width: '100%'
        }}
      >
        <Stack gap="xl">
          <DatePickerInput
            type="range"
            label="Meal plan dates"
            description="Pick a start and end date, up to 7 days"
            value={dateRange}
            allowSingleDateInRange={true}
            clearable
            valueFormat="M/D/YYYY"
            onChange={handleDateRangeChange}
            minDate={new Date()}
            weekendDays={[]}
            excludeDate={(dateString: string) => {
              if (!dateString) return false;

              // Parse the input date string manually to avoid timezone issues
              const [year, month, day] = dateString.split('-').map(Number);
              const inputDate = new Date(year, month - 1, day);

              // Mid-selection (start picked, end pending): gray out any date
              // that would make the range longer than 7 days, so an invalid
              // end can't even be clicked. Mantine sorts the pair, so a click
              // BEFORE the pending start is legal too; the window is 6 days
              // on either side.
              if (dateRange[0] && !dateRange[1]) {
                const [sy, sm, sd] = dateRange[0].split('-').map(Number);
                const pendingStart = new Date(sy, sm - 1, sd);
                const diffDays = Math.abs(
                  (inputDate.getTime() - pendingStart.getTime()) /
                    (1000 * 60 * 60 * 24)
                );
                if (diffDays > 6) return true;
              }

              const disabledDates = getDisabledDates();
              return disabledDates.some(disabledDate =>
                disabledDate.toDateString() === inputDate.toDateString()
              );
            }}
            renderDay={(dateString: string) => {
              if (!dateString) return <div></div>;

              // Parse date string manually to avoid timezone issues
              const [, , day] = dateString.split('-').map(Number);
              const coverageInfo = getDateCoverageInfo(dateString);

              // Determine indicator color based on meal plan status
              let indicatorColor = "orange"; // default
              if (coverageInfo.isCovered && coverageInfo.status === "draft") {
                indicatorColor = "indigo";
              } else if (coverageInfo.isCovered && coverageInfo.status === "active") {
                indicatorColor = "orange";
              }

              const tooltipLabel = coverageInfo.isCovered
                ? (coverageInfo.status === "draft"
                   ? "Date covered by draft meal plan"
                   : "Date covered by active meal plan")
                : null;

              return (
                <Tooltip
                  label={tooltipLabel}
                  disabled={!coverageInfo.isCovered}
                  position="top"
                  withArrow
                  color={indicatorColor}
                  events={{ hover: true, focus: true, touch: true }}
                >
                  <Indicator
                    size={6}
                    color={indicatorColor}
                    offset={-2}
                    disabled={!coverageInfo.isCovered}
                  >
                    <div>{day}</div>
                  </Indicator>
                </Tooltip>
              );
            }}
            size="md"
            styles={{
              input: {
                // Fluid font: the range value ("7/20/2026 – 7/26/2026") wraps
                // to two lines inside the input on ~320px screens at 16px
                fontSize: 'clamp(0.8125rem, 4vw, 1rem)',
                whiteSpace: 'nowrap',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center'
              }
            }}
          />

        </Stack>
      </Paper>

      {/* Flag ON: one card, one row per meal, every control scoped to its
          own row. Dinner is always planned; its fresh-vs-batch choice is the
          old "meal prep" feature, now explicitly dinner-scoped so its blast
          radius is visible. Flag OFF: the original standalone meal prep card
          below renders instead, unchanged. */}
      {additionalMealsEnabled && (
      <Paper
        radius="lg"
        p={{ base: 'lg', sm: 'xl' }}
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          width: '100%'
        }}
      >
        <Stack gap="md">
          <Group gap="sm" align="center">
            <ThemeIcon size={32} radius="md" color="orange" variant="light">
              <IconToolsKitchen2 size={18} />
            </ThemeIcon>
            <Box style={{ flex: 1 }}>
              <Text fw={600} size="lg" style={{ display: 'inline' }}>
                Which meals should we plan?
              </Text>
              {' '}
              <Badge size="sm" variant="light" color="violet" style={{ verticalAlign: 'middle' }}>
                Experimental
              </Badge>
              <Text size="sm" c="dimmed">
                Dinner is always included. Add breakfast or lunch and we'll keep them simple and repeatable. You can adjust or pencil over any day once your plan is ready.
              </Text>
            </Box>
          </Group>

          <Group gap={isNarrowScreen ? 6 : 'xs'} wrap="nowrap">
            <Chip
              size={isNarrowScreen ? 'sm' : 'md'}
              styles={isNarrowScreen ? NARROW_CHIP_STYLES : STABLE_CHIP_STYLES}
              checked={!!value.additionalMeals?.breakfast}
              onChange={(enabled) => {
                const next: AdditionalMealPreferences = {
                  ...value.additionalMeals,
                  breakfast: enabled
                    ? {
                        batch: cookingStyle?.breakfastMakeAhead ?? true,
                        ...(cookingStyle?.breakfastRotationSize !== undefined && {
                          rotationSize: cookingStyle.breakfastRotationSize,
                        }),
                      }
                    : undefined,
                };
                if (!next.breakfast) delete next.breakfast;
                if (!next.lunch) delete next.lunch;
                onChange({
                  ...value,
                  additionalMeals: next.breakfast || next.lunch ? next : undefined,
                });
              }}
            >
              Breakfasts
            </Chip>
            <Chip
              size={isNarrowScreen ? 'sm' : 'md'}
              styles={isNarrowScreen ? NARROW_CHIP_STYLES : STABLE_CHIP_STYLES}
              checked={!!value.additionalMeals?.lunch}
              onChange={(enabled) => {
                const next: AdditionalMealPreferences = {
                  ...value.additionalMeals,
                  lunch: enabled
                    ? (() => {
                        // Profile default; leftovers needs another night's
                        // cooked dinner, so single-day and fully-penciled
                        // plans always rotate
                        const mode =
                          cookingDays > 1 ? (cookingStyle?.lunchMode ?? 'leftovers') : 'rotation';
                        return mode === 'rotation'
                          ? {
                              mode,
                              batch: cookingStyle?.lunchMakeAhead ?? true,
                              ...(cookingStyle?.lunchRotationSize !== undefined && {
                                rotationSize: cookingStyle.lunchRotationSize,
                              }),
                            }
                          : { mode };
                      })()
                    : undefined,
                };
                if (!next.breakfast) delete next.breakfast;
                if (!next.lunch) delete next.lunch;
                onChange({
                  ...value,
                  additionalMeals: next.breakfast || next.lunch ? next : undefined,
                });
              }}
            >
              Lunches
            </Chip>
            <Tooltip label="Dinner is always included" withArrow>
              <Box>
                <Chip checked size={isNarrowScreen ? 'sm' : 'md'} onChange={() => {}} style={{ cursor: 'default' }} styles={isNarrowScreen ? NARROW_CHIP_STYLES : STABLE_CHIP_STYLES}>
                  Dinners
                </Chip>
              </Box>
            </Tooltip>
          </Group>

          {totalDays > 0 && (
            <Box>
              <Group gap={6} align="baseline">
                <Text size="sm" fw={600} c="dark">
                  How your week gets cooked
                </Text>
                {weekSummaryParts.some(part => part.target) && (
                  <Text size="xs" c="dimmed">
                    {isCoarsePointer ? 'tap' : 'click'} a part to adjust it
                  </Text>
                )}
              </Group>
              <Text size="sm" c="dimmed" mt={4} component="div">
                {/* Each part is an unbreakable unit with its trailing dot,
                    so a long summary wraps at separators instead of
                    mid-phrase. Tappable parts carry a dashed underline; the
                    open one goes orange. */}
                {weekSummaryParts.map((part, i) => (
                  <Fragment key={part.label}>
                    <Box component="span" style={{ whiteSpace: 'nowrap' }}>
                      {part.target ? (
                        <UnstyledButton
                          onClick={() =>
                            setActiveAdjust(current =>
                              current === part.target ? null : part.target!
                            )
                          }
                        >
                          <Text
                            component="span"
                            size="sm"
                            c={activeAdjust === part.target ? 'orange.7' : 'dimmed'}
                            fw={activeAdjust === part.target ? 600 : undefined}
                            style={{
                              textDecorationLine: 'underline',
                              textDecorationStyle: 'dashed',
                              textUnderlineOffset: 3,
                              textDecorationColor: 'var(--mantine-color-orange-5)',
                            }}
                          >
                            {part.label}
                          </Text>
                        </UnstyledButton>
                      ) : (
                        part.label
                      )}
                      {i < weekSummaryParts.length - 1 && ' \u00b7'}
                    </Box>
                    {i < weekSummaryParts.length - 1 && ' '}
                  </Fragment>
                ))}
              </Text>
            </Box>
          )}

          {/* One editor per clause, every editor permanently mounted and
              shown through its own Collapse, in the summary's chronological
              order. Mounting controls on demand broke Mantine's
              segmented-control indicator (it measures at mount, and a control
              born mid-animation measures nothing); height-clipped but laid
              out, like the old panel, they measure correctly. The swap is
              instant on purpose: animating two collapses at once (old
              closing, new opening) read as a double slide. Render guards
              handle a clause invalidated mid-edit (penciling away the cook
              nights, removing a meal). */}
          {additionalMealsEnabled && value.additionalMeals?.breakfast && (
            <Collapse in={activeAdjust === 'breakfast'} transitionDuration={0}>
              <Paper bg="gray.0" radius="md" p="md">
                <Stack gap="sm">
                  {/* The heading row carries the readout so the size control
                      needs no second label stacked under it */}
                  <Group justify="space-between">
                    <Text size="sm" fw={600}>Breakfasts</Text>
                    {maxRotationChoice > 1 && (
                      <Text size="sm" fw={600} c="orange.7">
                        {breakfastRotation} for {totalDays} day{totalDays !== 1 ? 's' : ''}
                      </Text>
                    )}
                  </Group>
                  {maxRotationChoice > 1 && (
                    <SegmentedControl
                      fullWidth
                      color="orange"
                      value={String(breakfastRotation)}
                      onChange={(v) =>
                        onChange({
                          ...value,
                          additionalMeals: {
                            ...value.additionalMeals,
                            breakfast: {
                              ...value.additionalMeals?.breakfast,
                              rotationSize: Number(v),
                            },
                          },
                        })
                      }
                      data={rotationChoices}
                    />
                  )}
                  <Switch
                    checked={value.additionalMeals.breakfast.batch !== false}
                    onChange={(e) =>
                      onChange({
                        ...value,
                        additionalMeals: {
                          ...value.additionalMeals,
                          breakfast: {
                            ...value.additionalMeals?.breakfast,
                            batch: e.currentTarget.checked,
                          },
                        },
                      })
                    }
                    color="orange"
                    size="sm"
                    label="Batch cook"
                    description="Cook each breakfast once and grab it on repeat days"
                  />
                </Stack>
              </Paper>
            </Collapse>
          )}
          {additionalMealsEnabled && value.additionalMeals?.lunch && (
            <Collapse in={activeAdjust === 'lunch'} transitionDuration={0}>
              <Paper bg="gray.0" radius="md" p="md">
                <Stack gap="sm">
                  <Text size="sm" fw={600}>Lunches</Text>
                  <SegmentedControl
                    fullWidth
                    orientation={isNarrowScreen ? 'vertical' : 'horizontal'}
                    color="orange"
                    value={value.additionalMeals.lunch.mode}
                    onChange={setLunchMode}
                    data={[
                      {
                        label: 'Dinner leftovers',
                        value: 'leftovers',
                        // Needs another night's cooked dinner: single-day
                        // plans and weeks penciled down to one cook night
                        disabled: cookingDays <= 1,
                      },
                      { label: 'Fresh lunches', value: 'rotation' },
                    ]}
                  />
                  <Text size="sm" c="dimmed">
                    {value.additionalMeals.lunch.mode === 'leftovers'
                      ? "We'll size up your dinners so leftovers cover the next day's lunch."
                      : 'A couple of easy lunches on rotation. They pack well for work or school.'}
                  </Text>
                  {value.additionalMeals.lunch.mode === 'rotation' && (
                    <>
                      {maxRotationChoice > 1 && (
                        <Box>
                          <Group justify="space-between" mb={4}>
                            <Text size="sm" fw={500}>
                              Different lunches
                            </Text>
                            <Text size="sm" fw={600} c="orange.7">
                              {lunchRotation} for {totalDays} day{totalDays !== 1 ? 's' : ''}
                            </Text>
                          </Group>
                          <SegmentedControl
                            fullWidth
                            color="orange"
                            value={String(lunchRotation)}
                            onChange={(v) =>
                              onChange({
                                ...value,
                                additionalMeals: {
                                  ...value.additionalMeals,
                                  lunch: {
                                    ...value.additionalMeals!.lunch!,
                                    mode: 'rotation',
                                    rotationSize: Number(v),
                                  },
                                },
                              })
                            }
                            data={rotationChoices}
                          />
                        </Box>
                      )}
                      <Switch
                        checked={value.additionalMeals.lunch.batch !== false}
                        onChange={(e) =>
                          onChange({
                            ...value,
                            additionalMeals: {
                              ...value.additionalMeals,
                              lunch: {
                                ...value.additionalMeals!.lunch!,
                                mode: 'rotation',
                                batch: e.currentTarget.checked,
                              },
                            },
                          })
                        }
                        color="orange"
                        size="sm"
                        label="Batch cook"
                        description="Cook each lunch once and pack it on repeat days"
                      />
                    </>
                  )}
                </Stack>
              </Paper>
            </Collapse>
          )}
          {dinnerAdjustable && (
            <Collapse in={activeAdjust === 'dinner'} transitionDuration={0}>
              <Paper bg="gray.0" radius="md" p="md">
                <Stack gap="sm">
                  <Group gap={8}>
                    <Text size="sm" fw={600}>Dinners</Text>
                    <Box ref={tooltipRef} component="span" style={{ display: 'inline-flex' }}>
                      <Tooltip
                        label={`Batch cooking means fewer, bigger dinner cooks that cover all ${cookingDays} nights. Fresh means a different dinner cooked each night.`}
                        multiline
                        w={250}
                        withArrow
                        position="bottom-start"
                        opened={showMealPrepTooltip}
                      >
                        <Box
                          component="span"
                          style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                          onClick={() => setShowMealPrepTooltip(!showMealPrepTooltip)}
                        >
                          <IconInfoCircle size={16} style={{ opacity: 0.5 }} />
                        </Box>
                      </Tooltip>
                    </Box>
                  </Group>
                  <SegmentedControl
                    fullWidth
                    orientation={isNarrowScreen ? 'vertical' : 'horizontal'}
                    color="orange"
                    value={value.uniqueMeals !== undefined ? 'batch' : 'fresh'}
                    onChange={(style) => {
                      if (style === 'batch') {
                        onChange({ ...value, uniqueMeals: defaultUniqueMeals });
                      } else {
                        onChange({ ...value, uniqueMeals: undefined, mealPrepPreferences: undefined });
                      }
                    }}
                    data={[
                      { label: 'Fresh every night', value: 'fresh' },
                      { label: 'Batch cook', value: 'batch' },
                    ]}
                  />
                  {value.uniqueMeals === undefined ? (
                    <Text size="sm" c="dimmed">
                      A different dinner cooked each night.
                    </Text>
                  ) : (
                    <Stack gap="md">
                      <Box>
                        {/* The summary clause above narrates the
                            cooks-to-nights consequence live; this is just the
                            knob with its current value */}
                        <Group justify="space-between" mb={4}>
                          <Text size="sm" fw={500}>
                            Dinners to cook
                          </Text>
                          <Text size="sm" fw={600} c="orange.7">
                            {uniqueMeals} for {cookingDays} night{cookingDays !== 1 ? 's' : ''}
                          </Text>
                        </Group>

                        {/* Degenerate on 2-day plans, where 1 is the only option */}
                        {maxUniqueMeals > 1 && (
                          <Slider
                            value={uniqueMeals}
                            onChange={(newUniqueMeals) => {
                              onChange({ ...value, uniqueMeals: newUniqueMeals });
                            }}
                            min={1}
                            max={maxUniqueMeals}
                            step={1}
                            size="lg"
                            color="orange"
                            marks={Array.from({ length: maxUniqueMeals }, (_, i) => ({
                              value: i + 1,
                              label: i + 1 === 1 ? '1' : i + 1 === maxUniqueMeals ? maxUniqueMeals.toString() : ''
                            }))}
                            styles={{
                              thumb: {
                                border: '2px solid white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                              }
                            }}
                            my="md"
                          />
                        )}
                      </Box>

                      <Textarea
                        label="Batch cooking notes (optional)"
                        placeholder="e.g. Prefer oven bakes, one-pot meals, similar ingredients across recipes, meals that freeze and reheat well, etc."
                        value={value.mealPrepPreferences?.generalPreferences || ''}
                        onChange={(e) => onChange({
                          ...value,
                          mealPrepPreferences: { ...value.mealPrepPreferences, generalPreferences: e.currentTarget.value }
                        })}
                        autosize
                        minRows={2}
                        maxRows={4}
                      />
                    </Stack>
                  )}
                </Stack>
              </Paper>
            </Collapse>
          )}
          {prepDaysAdjustable && (
            <Collapse in={activeAdjust === 'prepdays'} transitionDuration={0}>
              <Paper bg="gray.0" radius="md" p="md">
                <Stack gap="sm">
                  <Text size="sm" fw={600}>
                    Preferred cooking days (optional)
                  </Text>
                  <Chip.Group
                    multiple
                    value={value.prepDays ?? []}
                    onChange={(days) =>
                      onChange({ ...value, prepDays: days.length > 0 ? days : undefined })
                    }
                  >
                    <Group gap={6}>
                      {prepDateOptions.map(({ date, label }) => (
                        <Chip key={date} value={date} size="xs" styles={STABLE_CHIP_STYLES}>
                          {label}
                        </Chip>
                      ))}
                    </Group>
                  </Chip.Group>
                  <Text size="xs" c="dimmed">
                    We'll schedule your batch cooking (dinners and make-ahead meals) on these days whenever it keeps food fresh.
                  </Text>
                </Stack>
              </Paper>
            </Collapse>
          )}
        </Stack>
      </Paper>
      )}

      {/* Flag OFF: the original meal prep card, unchanged, so unflagged
          users see exactly the wizard they had */}
      {!additionalMealsEnabled && totalDays > 1 && (
        <Paper
          radius="lg"
          p={{ base: 'lg', sm: 'xl' }}
          style={{
            border: '1px solid var(--mantine-color-gray-2)',
            width: '100%'
          }}
        >
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Group gap="sm" align="center">
              <ThemeIcon size={32} radius="md" color="blue" variant="light">
                <IconChefHat size={18} />
              </ThemeIcon>
              <Group gap="xs" align="center" wrap="wrap" style={{ flex: 1, rowGap: 4 }}>
                <Text fw={600} size="lg">
                  Do you want to meal prep?
                </Text>
                <Box ref={tooltipRef} component="span" style={{ display: 'inline-flex' }}>
                  <Tooltip
                    label={`Cook in batches to save time. Choose how many meals you want to make for your ${totalDays}-day plan.`}
                    multiline
                    w={250}
                    withArrow
                    position="bottom-start"
                    opened={showMealPrepTooltip}
                  >
                    <Box
                      component="span"
                      style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => setShowMealPrepTooltip(!showMealPrepTooltip)}
                    >
                      <IconInfoCircle size={16} style={{ opacity: 0.5 }} />
                    </Box>
                  </Tooltip>
                </Box>
                <Badge size="sm" variant="light" color="violet">
                  Experimental
                </Badge>
              </Group>
            </Group>
            <Group gap="sm">
              <Button
                variant={!showMealPrepOptions ? "filled" : "subtle"}
                color="gray"
                size="sm"
                onClick={() => {
                  setShowMealPrepOptions(false);
                  // Clear meal prep preferences when saying "No" to meal prep
                  onChange({ ...value, uniqueMeals: undefined, mealPrepPreferences: undefined });
                }}
              >
                No
              </Button>
              <Button
                variant={showMealPrepOptions ? "filled" : "subtle"}
                color="blue"
                size="sm"
                onClick={() => {
                  setShowMealPrepOptions(true);
                  // Keep a valid existing choice, otherwise start at the default
                  if (value.uniqueMeals === undefined || value.uniqueMeals > maxUniqueMeals) {
                    onChange({ ...value, uniqueMeals: defaultUniqueMeals });
                  }
                }}
              >
                Yes
              </Button>
            </Group>
          </Group>

          <Collapse in={showMealPrepOptions} transitionDuration={200}>
            <Stack gap="lg" mt="md">
              {/* Unique Meals Slider */}
              <Box>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={500}>
                    Meals to cook
                  </Text>
                  <Badge size="lg" variant="filled" color="blue">
                    {uniqueMeals} meal{uniqueMeals !== 1 ? 's' : ''}
                  </Badge>
                </Group>

                {/* Degenerate on 2-day plans, where 1 is the only option */}
                {maxUniqueMeals > 1 && (
                  <Slider
                    value={uniqueMeals}
                    onChange={(newUniqueMeals) => {
                      onChange({ ...value, uniqueMeals: newUniqueMeals });
                    }}
                    min={1}
                    max={maxUniqueMeals}
                    step={1}
                    size="lg"
                    color="blue"
                    marks={Array.from({ length: maxUniqueMeals }, (_, i) => ({
                      value: i + 1,
                      label: i + 1 === 1 ? '1' : i + 1 === maxUniqueMeals ? maxUniqueMeals.toString() : ''
                    }))}
                    styles={{
                      thumb: {
                        border: '2px solid white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }}
                    my="xl"
                  />
                )}

                <Text size="sm" c="dimmed" ta="center" fw={500} mt={maxUniqueMeals > 1 ? undefined : 'md'}>
                  You'll cook {uniqueMeals} meal{uniqueMeals !== 1 ? 's' : ''} that will provide dinner for {cookingDays === totalDays ? `all ${totalDays}` : `the ${cookingDays} remaining`} days
                </Text>
              </Box>

              <Box>
                <Textarea
                  label="Tell us how you like to meal prep (optional)"
                  placeholder="e.g. Prefer oven cooking, similar ingredients across recipes, one-pot meals, cooking in batches, etc."
                  value={value.mealPrepPreferences?.generalPreferences || ''}
                  onChange={(e) => onChange({
                    ...value,
                    mealPrepPreferences: { ...value.mealPrepPreferences, generalPreferences: e.currentTarget.value }
                  })}
                  autosize
                  minRows={2}
                  maxRows={4}
                />
              </Box>
            </Stack>
          </Collapse>
        </Stack>
      </Paper>
      )}

      {/* Take-your-time days: week logistics, so it lives with dates and
          meal prep rather than in a review-step drawer. Shown even for
          single-day plans — it's the only way to mark that day as involved
          rather than the quick-meal default */}
      {dateRange[0] && dateRange[1] && (
        <SpecialConsiderationsStep
          embedded
          sections={['quickDays']}
          value={{ quickMealDays: value.quickMealDays, dateRange: value.dateRange }}
          onChange={(considerations: { quickMealDays?: string[] }) =>
            onChange({ ...value, quickMealDays: considerations.quickMealDays })
          }
          onNext={noop}
          onBack={noop}
          isLastStep={false}
          preferences={{ uniqueMeals: value.uniqueMeals }}
        />
      )}

      {/* Nights the family already has covered: each plan day is a chip,
          matching the take-your-time day row above — tap an open night to
          pencil a meal onto it, tap a claimed night to edit or un-pencil.
          Generation plans the remaining nights around them. */}
      {dateRange[0] && dateRange[1] && (
        <Paper radius="lg" p={{ base: 'lg', sm: 'xl' }} style={{ border: '1px solid var(--mantine-color-gray-2)', width: '100%' }}>
          <Stack gap="md">
            <Group gap="md" align="center">
              <ThemeIcon size={32} radius="md" color="grape" variant="light">
                <IconPencil size={18} />
              </ThemeIcon>
              <div style={{ flex: 1 }}>
                <Group gap="xs" align="center" wrap="wrap" style={{ rowGap: 4 }}>
                  <Text fw={600} size="lg" c="dark">
                    Any nights already covered?
                  </Text>
                  <Box ref={pencilTooltipRef} component="span" style={{ display: 'inline-flex' }}>
                    <Tooltip
                      label="Penciled-in nights don't need a recipe. Any ingredients you list go on your grocery list, and we plan the rest of the week around these nights."
                      multiline
                      w={250}
                      withArrow
                      position="bottom-start"
                      opened={showPencilTooltip}
                    >
                      <Box
                        component="span"
                        style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => setShowPencilTooltip(!showPencilTooltip)}
                      >
                        <IconInfoCircle size={16} style={{ opacity: 0.5 }} />
                      </Box>
                    </Tooltip>
                  </Box>
                  <Badge size="sm" variant="light" color="violet">
                    Experimental
                  </Badge>
                </Group>
                <Text size="sm" c="dimmed">Taco night, eating out, leftovers. {isCoarsePointer ? 'Tap' : 'Click'} a night to pencil it in and we'll plan the rest around it.</Text>
              </div>
            </Group>

            <Group gap="xs" wrap="wrap">
              {planDays.map(({ day, dateLabel }) => {
                const claimed = penciledMeals.find((m) => m.day === day);
                // A day with a planned event can't be penciled: generation
                // doesn't plan penciled days, so the event would be silently
                // dropped. One claim per day; remove the event first.
                const event = !claimed
                  ? plannedEvents?.find((e) => e.day === day)
                  : undefined;
                const chip = (
                  <Chip
                    checked={!!claimed}
                    onChange={() => !event && openPencilDrawer(day, dateLabel)}
                    variant="outline"
                    color="grape"
                    size="md"
                    disabled={!!event}
                    style={{
                      minWidth: 'fit-content',
                      cursor: event ? 'not-allowed' : 'pointer'
                    }}
                    styles={{
                      label: {
                        paddingTop: 25,
                        paddingBottom: 25
                      }
                    }}
                  >
                    <Stack gap={4} align="center">
                      <Text size="xs">{day.slice(0, 3)}</Text>
                      <Text size="9px" opacity={0.5}>{dateLabel}</Text>
                    </Stack>
                  </Chip>
                );
                if (!event) return <Box key={day}>{chip}</Box>;
                // Disabled chips swallow pointer events, so the tooltip
                // listens on a wrapper instead
                return (
                  <Tooltip
                    key={day}
                    label={event.occasion ? `Has an event: ${event.occasion}` : 'Has an event planned'}
                    position="top"
                    withArrow
                    events={{ hover: true, focus: true, touch: true }}
                  >
                    <Box>{chip}</Box>
                  </Tooltip>
                );
              })}
            </Group>

            {penciledMeals.length > 0 ? (
              <Text size="xs" c="grape.7">
                ✓ {planDays
                  .filter(({ day }) => penciledMeals.some((m) => m.day === day))
                  .map(({ day }) => {
                    const meal = penciledMeals.find((m) => m.day === day)!;
                    return `${day.slice(0, 3)} ${meal.name}`;
                  })
                  .join(' · ')}. {(() => {
                  const remaining = Math.max(totalDays - penciledMeals.length, 0);
                  if (remaining === 0) return "You've got the whole week covered.";
                  if (remaining === 1) return "We'll plan the other night.";
                  return `We'll plan the other ${remaining} nights.`;
                })()}
              </Text>
            ) : (
              <Text size="xs" c="dimmed">
                We'll plan every night.
              </Text>
            )}
          </Stack>
        </Paper>
      )}

      <CustomMealDrawer
        opened={pencilDrawer !== null}
        onClose={() => setPencilDrawer(null)}
        dayLabel={pencilDrawer ? `${pencilDrawer.day} ${pencilDrawer.dateLabel}` : ''}
        mode={editingMeal ? 'edit' : 'add'}
        initial={editingMeal}
        savedMeals={savedPenciledMeals ?? undefined}
        daySelect={pencilDrawer ? { options: [pencilDrawer], initialDay: pencilDrawer.day } : undefined}
        onRemove={editingMeal && pencilDrawer ? () => removePenciledDay(pencilDrawer.day) : undefined}
        saving={false}
        onSubmit={handlePencilSubmit}
      />

      {/* Status Messages */}
      {dateRange[0] && dateRange[1] && (
        <Paper
          p="lg"
          radius="lg"
          bg="gray.0"
          style={{
            border: '1px solid var(--mantine-color-gray-2)',
            minHeight: 80,
            width: '100%'
          }}
        >
          <Group gap="md" align="center" style={{ height: '100%' }}>
            <ThemeIcon
              size={40}
              radius="md"
              color="green"
              variant="light"
            >
              <IconCalendar size={20} />
            </ThemeIcon>

            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} c="green.7" style={{ wordBreak: 'break-word' }}>
                Dates selected
              </Text>
              <Text size="sm" c="dimmed" style={{ wordBreak: 'break-word' }}>
                You've chosen a {
                  (() => {
                    // Parse dates manually to avoid timezone issues
                    const [startYear, startMonth, startDay] = dateRange[0].split('-').map(Number);
                    const [endYear, endMonth, endDay] = dateRange[1].split('-').map(Number);
                    const startDate = new Date(startYear, startMonth - 1, startDay);
                    const endDate = new Date(endYear, endMonth - 1, endDay);
                    return Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                  })()
                }-day meal plan from {formatDateForDisplay(dateRange[0])} to {formatDateForDisplay(dateRange[1])}.
              </Text>
              {/* The step also decides which meals get planned, so the
                  confirmation names them, with the cooks-to-meals payoff
                  when breakfast or lunch is in the mix (on dinner-only
                  plans the ratio is meal-for-meal and says nothing) */}
              {additionalMealsEnabled && (
                <Text size="sm" c="dimmed" style={{ wordBreak: 'break-word' }}>
                  We'll plan{' '}
                  {plannedBreakfast && plannedLunch
                    ? 'dinners, breakfasts, and lunches'
                    : plannedBreakfast
                      ? 'dinners and breakfasts'
                      : plannedLunch
                        ? 'dinners and lunches'
                        : 'dinners'}
                  .
                  {(plannedBreakfast || plannedLunch) &&
                    ` ${totalMeals} meals from ${totalCooks} cook${totalCooks !== 1 ? 's' : ''}.`}
                </Text>
              )}
            </Stack>
          </Group>
        </Paper>
      )}

      {/* Navigation */}
      <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
        <Button
          variant="subtle"
          size="md"
          leftSection={<IconArrowLeft size={18} />}
          onClick={onBack}
          color="orange"
        >
          Back
        </Button>

        <Button
          size="md"
          rightSection={<IconArrowRight size={18} />}
          onClick={handleNext}
          disabled={!canProceed}
          color="orange"
          variant={!canProceed ? "light" : "filled"}
        >
          Continue
        </Button>
      </Group>
    </Stack>
  );
};
